import { Spacing, Text, Title } from '@vkontakte/vkui';
import cover from 'img/subscriptions/cancel-autorenew-family.png';
import coverEmpty from 'img/subscriptions/cancel-autorenew-family-empty.png';
import coverEmptyMobile from 'img/subscriptions/cancel-autorenew-family-empty-mobile.png';
import coverMobile from 'img/subscriptions/cancel-autorenew-family-mobile.png';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { inviteToFamilyOrCreate } from 'reactApp/modules/family/family.actions';
import { isFamilyEmpty, isUserFamilyOwner } from 'reactApp/modules/family/family.selectors';
import { getFeatureAbCancelSubsWithCaptcha } from 'reactApp/modules/features/features.selectors';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { Button } from 'reactApp/ui/Button/Button';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import styles from './CancelAutoRenewFamilyModal.css';
import type { CancelAutoRenewFamilyModalProps } from './CancelAutoRenewFamilyModal.types';

export const CancelAutoRenewFamilyModal = memo<CancelAutoRenewFamilyModalProps>(({ subscription, onClose, onCancel, onSuccess }) => {
    const dispatch = useDispatch();

    const isMobile = EnvironmentSelectors.isPhone();
    const scrollRef = useRef<HTMLDivElement>(null);

    const isFamilyOwner = useSelector(isUserFamilyOwner);
    const isEmptyFamily = useSelector(isFamilyEmpty);
    const notEmptyFamilyOwner = isFamilyOwner && !isEmptyFamily;
    // tempexp_13984-next-line
    const abWithCaptcha = useSelector(getFeatureAbCancelSubsWithCaptcha);
    const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);

    const title = notEmptyFamilyOwner ? 'В вашей подписке есть участники' : 'Одна цена на четверых';
    const text = notEmptyFamilyOwner ? (
        <>После отключения они тоже не&nbsp;смогут ей&nbsp;пользоваться и&nbsp;вернутся к&nbsp;бесплатным аккаунтам</>
    ) : (
        <>
            Добавьте в&nbsp;подписку до&nbsp;3&nbsp;участников&nbsp;&mdash; цена при этом не&nbsp;изменится. Место станет общим,
            но&nbsp;у&nbsp;каждого будет доступ только к&nbsp;своим файлам
        </>
    );
    const image = notEmptyFamilyOwner ? cover : coverEmpty;
    const imageMobile = notEmptyFamilyOwner ? coverMobile : coverEmptyMobile;
    const primaryButtonText = notEmptyFamilyOwner ? 'Сохранить подписку' : 'Добавить участника';

    useEffect(() => {
        emitAnalyticEvent(notEmptyFamilyOwner ? AnalyticEventNames.CANCEL_SUB_FAMILY : AnalyticEventNames.CANCEL_SUB_EMPTY_FAMILY);
        sendAnalytics(notEmptyFamilyOwner ? ESubscriptionModalAction.family : ESubscriptionModalAction.familyEmpty);
    }, []);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={16} />
                <Text className={styles.text}>{text}</Text>
                <Spacing size={24} />
            </>
        );
    }, [notEmptyFamilyOwner, text]);

    const handleAddMember = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.CANCEL_SUB_EMPTY_FAMILY_CLICK);
        sendAnalytics(ESubscriptionModalAction.familyEmptyAdd);
        dispatch(inviteToFamilyOrCreate());
        onSuccess();
    }, [onSuccess, dispatch, sendAnalytics]);

    const handleKeepSubscription = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.CANCEL_SUB_FAMILY_CLICK);
        sendAnalytics(ESubscriptionModalAction.familySave);
        onSuccess();
    }, [onSuccess, sendAnalytics]);

    const handleCancelSubscription = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.CANCEL_SUB_FAMILY_CANCEL);
        sendAnalytics(ESubscriptionModalAction.familyCancel);
        onCancel();
    }, [onCancel, dispatch, abWithCaptcha, subscription, sendAnalytics, notEmptyFamilyOwner]);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleClick = notEmptyFamilyOwner ? handleKeepSubscription : handleAddMember;

    if (isMobile) {
        return (
            <MobileDialog
                id="cancel-autorenew-family-modal-mobile"
                onClose={handleClose}
                closeOnDimmerClick
                scrollRef={scrollRef}
                mod="base"
                contextPadding="zero"
            >
                <div ref={scrollRef} className={styles.rootMobile}>
                    <img className={styles.image} src={imageMobile} alt={title} />
                    <div className={styles.content}>
                        <Title>{title}</Title>
                        <Spacing size={12} />
                        <Text className={styles.text}>{text}</Text>
                    </div>

                    <div className={styles.buttonWrapper}>
                        <Button theme="vk" primary fluid onClick={handleClick}>
                            {primaryButtonText}
                        </Button>
                        <Spacing size={12} />
                        <Button theme="vk" fluid onClick={handleCancelSubscription}>
                            Отключить
                        </Button>
                    </div>
                </div>
            </MobileDialog>
        );
    }

    return (
        <WhatsNewDialog
            title={title}
            content={content}
            imageUrl={image}
            primaryButtonText={primaryButtonText}
            onClick={handleClick}
            secondaryButtonText="Отключить"
            onSecondary={handleCancelSubscription}
            onClose={handleClose}
            imageBgColor="transparent"
            buttonTheme="vk"
            dialogSize="tiny"
            qaId="cancel-autorenew-family-modal"
        />
    );
});

CancelAutoRenewFamilyModal.displayName = 'CancelAutoRenewFamilyModal';
